import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { X, AlertCircle, CheckCircle } from "lucide-react";

interface HealthScore {
  overall: number;
  clarity: number;
  medicalAccuracy: number;
  structure: number;
  completeness: number;
  issues: Array<{
    type: 'warning' | 'error' | 'info';
    message: string;
    suggestion: string;
  }>;
}

interface ContentHealthScorerProps {
  score: HealthScore;
  onClose: () => void;
}

const getScoreColor = (score: number) => {
  if (score >= 90) return "text-green-600";
  if (score >= 75) return "text-yellow-600";
  return "text-red-600";
};

const getScoreLabel = (score: number) => {
  if (score >= 90) return "Excellent";
  if (score >= 75) return "Good";
  if (score >= 60) return "Fair";
  return "Needs Improvement";
};

export const ContentHealthScorer = ({ score, onClose }: ContentHealthScorerProps) => {
  return (
    <Card className="mt-4 border-primary/20">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Content Health Score</span>
          <Button size="sm" variant="ghost" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overall Score */}
        <div className="text-center">
          <div className={`text-6xl font-bold ${getScoreColor(score.overall)}`}>
            {score.overall}
          </div>
          <div className="text-lg text-muted-foreground mt-2">
            {getScoreLabel(score.overall)}
          </div>
        </div>

        {/* Individual Metrics */}
        <div className="space-y-4">
          <div>
            <div className="flex justify-between mb-2">
              <span className="text-sm font-medium">Clarity</span>
              <span className={`text-sm font-bold ${getScoreColor(score.clarity)}`}>
                {score.clarity}/100
              </span>
            </div>
            <Progress value={score.clarity} className="h-2" />
          </div>

          <div>
            <div className="flex justify-between mb-2">
              <span className="text-sm font-medium">Medical Accuracy</span>
              <span className={`text-sm font-bold ${getScoreColor(score.medicalAccuracy)}`}>
                {score.medicalAccuracy}/100
              </span>
            </div>
            <Progress value={score.medicalAccuracy} className="h-2" />
          </div>

          <div>
            <div className="flex justify-between mb-2">
              <span className="text-sm font-medium">Structure</span>
              <span className={`text-sm font-bold ${getScoreColor(score.structure)}`}>
                {score.structure}/100
              </span>
            </div>
            <Progress value={score.structure} className="h-2" />
          </div>

          <div>
            <div className="flex justify-between mb-2">
              <span className="text-sm font-medium">Completeness</span>
              <span className={`text-sm font-bold ${getScoreColor(score.completeness)}`}>
                {score.completeness}/100
              </span>
            </div>
            <Progress value={score.completeness} className="h-2" />
          </div>
        </div>

        {/* Issues and Suggestions */}
        {score.issues && score.issues.length > 0 && (
          <div className="space-y-3 pt-4 border-t">
            <h4 className="font-semibold text-sm">Issues & Suggestions</h4>
            {score.issues.map((issue, index) => (
              <div
                key={index}
                className="flex gap-3 p-3 rounded-lg bg-muted/50"
              >
                {issue.type === 'error' ? (
                  <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                ) : issue.type === 'warning' ? (
                  <AlertCircle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                ) : (
                  <CheckCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className="text-sm font-medium">{issue.message}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {issue.suggestion}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
